import { useState } from "react";
import { useLocation } from "wouter";
import { AdvicelineNurseLayout } from "@/components/layout/adviceline-nurse-layout";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Phone, ArrowLeft, CheckCircle2, Loader2, AlertTriangle, 
  CalendarDays,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type Category = "advice" | "prescription" | "appointment" | "flare" | "blood_test_form" | "medication" | "homecare" | "infusion_query" | "chasing_results" | "blood_test_result" | "other";
type Outcome = "resolved_on_call" | "needs_follow_up" | "referred_to_consultant" | "prescription_sent" | "blood_forms_sent" | "other";

const CATEGORIES: { value: Category; label: string }[] = [
  { value: "advice", label: "Advice" },
  { value: "prescription", label: "Prescription" },
  { value: "appointment", label: "Appointment" },
  { value: "flare", label: "Flare" },
  { value: "blood_test_form", label: "Blood Test Form" },
  { value: "medication", label: "Medication" },
  { value: "homecare", label: "Homecare" },
  { value: "infusion_query", label: "Infusion Query" },
  { value: "chasing_results", label: "Chasing Results" },
  { value: "blood_test_result", label: "Blood Test Result" },
  { value: "other", label: "Other" },
];

const OUTCOMES: { value: Outcome; label: string }[] = [
  { value: "resolved_on_call", label: "Resolved on call" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "referred_to_consultant", label: "Referred to consultant" },
  { value: "prescription_sent", label: "Prescription sent" },
  { value: "blood_forms_sent", label: "Blood forms sent" },
  { value: "other", label: "Other" },
];

export default function LogCallPage() {
  const [, navigate] = useLocation();
  const [patientName, setPatientName] = useState("");
  const [patientDob, setPatientDob] = useState("");
  const [nhsNumber, setNhsNumber] = useState("");
  const [category, setCategory] = useState<Category | "">("");
  const [outcome, setOutcome] = useState<Outcome | "">("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");
  const Layout = isAdvicelineHub ? AdvicelineNurseLayout : NurseLayout;

  const isValid = patientName.trim().length > 0 && category !== "" && notes.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${BASE}/api/nurse/patient-requests/quick-log`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patientName: patientName.trim(),
          patientDob: patientDob || null,
          nhsNumber: nhsNumber || null,
          category,
          outcome: outcome || "resolved_on_call",
          notes: notes.trim(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to log call" }));
        throw new Error(err.error ?? "Failed to log call");
      }

      setSubmitted(true);
      (window as any).umami?.track?.("quick-log-created", { category, outcome: outcome || "resolved_on_call" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to log call");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setPatientName("");
    setPatientDob("");
    setNhsNumber("");
    setCategory("");
    setOutcome("");
    setNotes("");
    setSubmitted(false);
    setError(null);
  };

  return (
    <Layout title="Log a Call">
      <div className="max-w-2xl">
        <button
          onClick={() => navigate("/nurse")}
          className="text-slate-500 hover:text-slate-900 text-sm flex items-center gap-1 mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to requests
        </button>

        {submitted ? (
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-6 flex items-start gap-4">
              <CheckCircle2 className="h-7 w-7 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-1">Call logged</h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Your phone call with {patientName} has been recorded. It will appear in your incoming requests with a phone log entry.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleReset} variant="outline" className="rounded-full">Log another call</Button>
              <Button onClick={() => navigate("/nurse")} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Back to requests</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-emerald-100 p-2.5 rounded-xl">
                <Phone className="h-6 w-6 text-emerald-700" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Log a Phone Call</h1>
                <p className="text-sm text-slate-500">Record a patient phone call in 30 seconds</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="pname" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Patient name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="pname"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                  className="rounded-2xl"
                  autoFocus
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dob" className="text-sm font-medium text-slate-700 mb-1.5 block">Date of birth</Label>
                  <Input
                    id="dob"
                    value={patientDob}
                    onChange={(e) => setPatientDob(e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className="rounded-2xl"
                  />
                </div>
                <div>
                  <Label htmlFor="nhs" className="text-sm font-medium text-slate-700 mb-1.5 block">NHS number</Label>
                  <Input
                    id="nhs"
                    value={nhsNumber}
                    onChange={(e) => setNhsNumber(e.target.value)}
                    placeholder="e.g. 485 777 3456"
                    className="rounded-2xl"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  What was this about? <span className="text-red-500">*</span>
                </Label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setCategory(c.value)}
                      className={`text-left px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                        category === c.value
                          ? "bg-emerald-50 border-emerald-300 text-emerald-900 font-medium"
                          : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Outcome</Label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {OUTCOMES.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setOutcome(o.value)}
                      className={`text-left px-4 py-2.5 rounded-xl text-sm border transition-colors ${
                        outcome === o.value
                          ? "bg-sky-50 border-sky-300 text-sky-900 font-medium"
                          : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="notes" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Notes <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What was discussed? What action was taken? (2-3 lines is enough)"
                  className="rounded-2xl min-h-[80px] resize-none"
                  maxLength={2000}
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{notes.length}/2000</p>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            <Button
              onClick={handleSubmit}
              disabled={!isValid || isSubmitting}
              className="rounded-full px-8 gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-200"
            >
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Logging…</>
              ) : (
                <><CheckCircle2 className="h-4 w-4" /> Log call</>
              )}
            </Button>

            <p className="text-xs text-slate-400">
              This creates a timestamped record of a phone call. It will appear in your incoming requests list for audit and tracking purposes. Signed off automatically with your name and the current time.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
